import { useEffect } from 'react';

/**
 * Hook for adding a beforeunload event listener to the window.
 *
 * @param cb - Callback function to be called when the beforeunload event is triggered
 * @param when - Whether the listener should be added or removed
 *
 * @example
 * ```tsx
 * import { useBeforeUnload } from 'my-library';
 *
 * function MyComponent() {
 *   useBeforeUnload(() => {
 *     console.log('Unloading the page!');
 *   }, true);
 *
 *   return <div>Hello, world!</div>;
 * }
 * ```
 */
export function useBeforeUnload(cb: () => void, when: boolean): void {
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (when) {
        event.preventDefault();
        cb();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [cb, when]);
}
